import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { useNavigate } from 'react-router'
import { Choice } from '@/components/Choice'
import { FormError } from '@/components/FormError'
import { Steps } from '@/components/Steps'
import { ShareWith } from '@/components/ShareWith'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { api } from '@/lib/api'
import { organize, type Collection } from '@/lib/albums'
import type { Family, Space } from '@/lib/families'
import { handOffPhotos } from '@/lib/handoff'
import { audienceBody, startingAudience, useLastShare, type Audience } from '@/lib/spaces'

type Step = 'name' | 'who' | 'shelf'

// Starting an album, a step at a time: what it's called, who it's for (starting from the space you're in), and
// whether it goes on a shelf. Then straight in to add the photos.
export function NewAlbum({
  open,
  onOpenChange,
  family,
  space,
  collections,
  first,
  photos,
  send,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  family: Family
  space: Space | null
  collections: Collection[]
  first: boolean
  // Photos already picked, to add as soon as the album opens.
  photos?: File[]
  // Photos already added, picked to send to the new album once it's made.
  send?: string[]
}) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const steps: Step[] = ['name', 'who', 'shelf']
  const [step, setStep] = useState<Step>('name')
  const [title, setTitle] = useState('')
  const [about, setAbout] = useState('')
  const lastShare = useLastShare()
  const [who, setWho] = useState<Audience>(() => lastShare.start(startingAudience(family, space)))
  const [shelf, setShelf] = useState('')
  const [shelfName, setShelfName] = useState('')
  const at = steps.indexOf(step)
  const last = at === steps.length - 1
  const create = useMutation({
    mutationFn: async () => {
      const collectionId =
        shelf === 'new'
          ? (
              await api<{ id: string }>(`/families/${family.id}/collections`, {
                method: 'POST',
                body: { title: shelfName.trim() },
              })
            ).id
          : shelf || undefined
      return api<{ id: string }>(`/families/${family.id}/albums`, {
        method: 'POST',
        body: { title: title.trim(), description: about.trim(), ...audienceBody(who), collectionId },
      })
    },
    onSuccess: async ({ id }) => {
      lastShare.remember(who)
      if (send?.length) await organize(family.id, send, { albumId: id })
      for (const key of ['albums', 'stream', 'found-photos', 'album', 'photo']) {
        await queryClient.invalidateQueries({ queryKey: [key] })
      }
      if (photos?.length) handOffPhotos(id, photos)
      navigate(`/album/${id}`)
    },
  })

  function submit(e: FormEvent) {
    e.preventDefault()
    if (last) create.mutate()
    else setStep(steps[at + 1]!)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-lg">
        <form onSubmit={submit} className="flex flex-col gap-6">
          <DialogHeader>
            <p className="eyebrow">
              {first ? 'The first album' : 'New album'} · Step {at + 1} of {steps.length}
            </p>
            <DialogTitle>
              {step === 'name' ? 'What’s it called?' : step === 'who' ? 'Who’s this for?' : 'Put it in a collection?'}
            </DialogTitle>
            <DialogDescription>
              {step === 'name'
                ? 'Name it for the gathering or the season. You can rename it any time.'
                : step === 'who'
                  ? 'Only the people you pick will see it, and what’s in it. You can change this any time.'
                  : 'A collection gathers albums together, like Holidays or Summers. It doesn’t change who sees them.'}
            </DialogDescription>
          </DialogHeader>
          <Steps n={steps.length} at={at} />

          {step === 'name' && (
            <div className="grid gap-3">
              <Label htmlFor="album-title">Album name</Label>
              <Input
                id="album-title"
                required
                maxLength={80}
                value={title}
                onChange={(e) => setTitle(e.target.value)}
              />
              <Label htmlFor="album-about" className="mt-3">
                A few words about it <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Textarea id="album-about" maxLength={1000} value={about} onChange={(e) => setAbout(e.target.value)} />
            </div>
          )}

          {step === 'who' && (
            <ShareWith here={family.id} value={who} onChange={setWho} onlyMeHint="Just you, until you share it" />
          )}

          {step === 'shelf' && (
            <div className="grid gap-4">
              <Choice
                legend="Collection"
                value={shelf}
                options={[
                  ['', 'No'],
                  ...collections.map((x): [string, string] => [x.id, x.title]),
                  ['new', 'A new one'],
                ]}
                onChange={setShelf}
              />
              {shelf === 'new' && (
                <div className="grid gap-2">
                  <Label htmlFor="collection-title">Name the collection</Label>
                  <Input
                    id="collection-title"
                    required
                    maxLength={80}
                    value={shelfName}
                    onChange={(e) => setShelfName(e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          <FormError error={create.error} />
          <div className="flex flex-row-reverse flex-wrap justify-between gap-2">
            <Button type="submit" size="lg" disabled={create.isPending || (step === 'name' && !title.trim())}>
              {last ? 'Start album' : 'Next'}
            </Button>
            {at > 0 && (
              <Button type="button" variant="ghost" size="lg" onClick={() => setStep(steps[at - 1]!)}>
                Back
              </Button>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
